import { LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import type { EventLogEntry } from "./app-events.ts";
import type { AppViewState } from "./app-view-state.ts";
import type { DevicePairingList } from "./controllers/devices.ts";
import type { ExecApprovalRequest } from "./controllers/exec-approval.ts";
import type { ExecApprovalsFile, ExecApprovalsSnapshot } from "./controllers/exec-approvals.ts";
import type { ProjectSessionEntry } from "./controllers/igloobot.ts";
import type { SkillParametersResult, SkillSummary } from "./controllers/skills-settings.ts";
import type { SkillMessage } from "./controllers/skills.ts";
import type { GatewayBrowserClient, GatewayHelloOk } from "./gateway.ts";
import type { Tab } from "./navigation.ts";
import type { ResolvedTheme, ThemeMode } from "./theme.ts";
import type {
  AgentsListResult,
  AgentsFilesListResult,
  AgentIdentityResult,
  ConfigSnapshot,
  ConfigUiHints,
  CronJob,
  CronRunLogEntry,
  CronStatus,
  HealthSnapshot,
  LogEntry,
  LogLevel,
  PresenceEntry,
  ChannelsStatusSnapshot,
  SessionsListResult,
  SkillStatusReport,
  StatusSummary,
} from "./types.ts";
import type {
  AvatarConfig,
  AvatarServiceStatus,
  AvatarSessionEntry,
} from "./views/avatar-admin.ts";
import type { ProjectEntry } from "./views/projects.ts";
import type { SkillEntry } from "./views/skills-admin.ts";
import type { TaskEntry } from "./views/tasks-admin.ts";
import type { UserEntry, UserProjectAssignment } from "./views/users-admin.ts";
import {
  handleChannelConfigReload as handleChannelConfigReloadInternal,
  handleChannelConfigSave as handleChannelConfigSaveInternal,
  handleWhatsAppLogout as handleWhatsAppLogoutInternal,
  handleWhatsAppStart as handleWhatsAppStartInternal,
  handleWhatsAppWait as handleWhatsAppWaitInternal,
} from "./app-channels.ts";
import {
  handleAbortChat as handleAbortChatInternal,
  handleSendChat as handleSendChatInternal,
  removeQueuedMessage as removeQueuedMessageInternal,
} from "./app-chat.ts";
import { DEFAULT_CRON_FORM, DEFAULT_LOG_LEVEL_FILTERS } from "./app-defaults.ts";
import { connectGateway as connectGatewayInternal } from "./app-gateway.ts";
import {
  handleConnected,
  handleDisconnected,
  handleFirstUpdated,
  handleUpdated,
} from "./app-lifecycle.ts";
import { renderApp } from "./app-render.ts";
import {
  exportLogs as exportLogsInternal,
  handleChatScroll as handleChatScrollInternal,
  handleLogsScroll as handleLogsScrollInternal,
  resetChatScroll as resetChatScrollInternal,
  scheduleChatScroll as scheduleChatScrollInternal,
} from "./app-scroll.ts";
import {
  applySettings as applySettingsInternal,
  loadCron as loadCronInternal,
  loadOverview as loadOverviewInternal,
  setTab as setTabInternal,
  setTheme as setThemeInternal,
  onPopState as onPopStateInternal,
} from "./app-settings.ts";
import {
  loadOverviewData,
  createOverviewDataState,
  type OverviewDataState,
} from "./controllers/overview.ts";
import {
  resetToolStream as resetToolStreamInternal,
  type ToolStreamEntry,
  type CompactionStatus,
} from "./app-tool-stream.ts";
import { resolveInjectedAssistantIdentity } from "./assistant-identity.ts";
import { loadAssistantIdentity as loadAssistantIdentityInternal } from "./controllers/assistant-identity.ts";
import {
  loadSettings,
  loadAuth,
  saveAuth,
  clearAuth,
  type UiSettings,
  type AuthUser,
} from "./storage.ts";
import { type ChatAttachment, type ChatQueueItem, type CronFormState } from "./ui-types.ts";

declare global {
  interface Window {
    __IGLOOBOT_CONTROL_UI_BASE_PATH__?: string;
  }
}

const injectedAssistantIdentity = resolveInjectedAssistantIdentity();

function resolveOnboardingMode(): boolean {
  if (!window.location.search) {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("onboarding");
  if (!raw) {
    return false;
  }
  const normalized = raw.trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "on";
}

@customElement("igloobot-app")
export class IglooBotApp extends LitElement {
  @state() settings: UiSettings = loadSettings();
  @state() password = "";
  @state() tab: Tab = "chat";

  // Auth state (RBAC login)
  @state() isAuthenticated = loadAuth().authToken !== null;
  @state() authToken: string | null = loadAuth().authToken;
  @state() currentUser: AuthUser | null = loadAuth().currentUser;
  @state() onboarding = resolveOnboardingMode();
  @state() connected = false;
  @state() theme: ThemeMode = this.settings.theme ?? "system";
  @state() themeResolved: ResolvedTheme = "dark";
  @state() hello: GatewayHelloOk | null = null;
  @state() lastError: string | null = null;
  @state() eventLog: EventLogEntry[] = [];
  private eventLogBuffer: EventLogEntry[] = [];
  private toolStreamSyncTimer: number | null = null;
  private sidebarCloseTimer: number | null = null;

  @state() assistantName = injectedAssistantIdentity.name;
  @state() assistantAvatar = injectedAssistantIdentity.avatar;
  @state() assistantAgentId = injectedAssistantIdentity.agentId ?? null;

  @state() sessionKey = this.settings.sessionKey;
  @state() chatLoading = false;
  @state() chatSending = false;
  @state() chatMessage = "";
  @state() chatMessages: unknown[] = [];
  @state() chatToolMessages: unknown[] = [];
  @state() chatStream: string | null = null;
  @state() chatStreamStartedAt: number | null = null;
  @state() chatRunId: string | null = null;
  @state() compactionStatus: CompactionStatus | null = null;
  @state() chatAvatarUrl: string | null = null;
  @state() chatThinkingLevel: string | null = null;
  @state() chatQueue: ChatQueueItem[] = [];
  @state() chatAttachments: ChatAttachment[] = [];
  @state() chatManualRefreshInFlight = false;
  // Slash-command autocomplete state
  @state() chatCommands: { name: string; description: string; skillName: string }[] = [];
  @state() chatCmdMenuOpen = false;
  @state() chatCmdIndex = 0;
  @state() chatConsoleVisible = true;
  @state() chatConsoleSkills: Array<{ name: string; label: string }> = [];
  @state() chatConsoleLoading = false;
  @state() previewFilePath: string | null = null;
  @state() previewFileName: string | null = null;
  @state() previewFileType: "pdf" | "image" | "markdown" | "text" | "other" | null = null;
  // Sidebar state for tool output viewing
  @state() sidebarOpen = false;
  @state() sidebarContent: string | null = null;
  @state() sidebarError: string | null = null;
  @state() splitRatio = this.settings.splitRatio;

  @state() nodesLoading = false;
  @state() nodes: Array<Record<string, unknown>> = [];
  @state() devicesLoading = false;
  @state() devicesError: string | null = null;
  @state() devicesList: DevicePairingList | null = null;
  @state() execApprovalsLoading = false;
  @state() execApprovalsSaving = false;
  @state() execApprovalsDirty = false;
  @state() execApprovalsSnapshot: ExecApprovalsSnapshot | null = null;
  @state() execApprovalsForm: ExecApprovalsFile | null = null;
  @state() execApprovalsSelectedAgent: string | null = null;
  @state() execApprovalsTarget: "gateway" | "node" = "gateway";
  @state() execApprovalsTargetNodeId: string | null = null;
  @state() execApprovalQueue: ExecApprovalRequest[] = [];
  @state() execApprovalBusy = false;
  @state() execApprovalError: string | null = null;
  @state() pendingGatewayUrl: string | null = null;

  @state() configLoading = false;
  @state() configRaw = "{\n}\n";
  @state() configRawOriginal = "";
  @state() configValid: boolean | null = null;
  @state() configIssues: unknown[] = [];
  @state() configSaving = false;
  @state() configApplying = false;
  @state() updateRunning = false;
  @state() applySessionKey = this.settings.lastActiveSessionKey;
  @state() configSnapshot: ConfigSnapshot | null = null;
  @state() configSchema: unknown = null;
  @state() configSchemaVersion: string | null = null;
  @state() configSchemaLoading = false;
  @state() configUiHints: ConfigUiHints = {};
  @state() configForm: Record<string, unknown> | null = null;
  @state() configFormOriginal: Record<string, unknown> | null = null;
  @state() configFormDirty = false;
  @state() configFormMode: "form" | "raw" = "form";
  @state() configSearchQuery = "";
  @state() configActiveSection: string | null = null;
  @state() configActiveSubsection: string | null = null;
  @state() settingsSearchQuery = "";
  @state() settingsActiveSection: string | null = null;
  @state() settingsActiveSubsection: string | null = null;
  @state() configSectionsCollapsed: Record<string, boolean> = {};
  @state() settingsSectionsCollapsed: Record<string, boolean> = {};
  @state() channelsLoading = false;
  @state() channelsSnapshot: ChannelsStatusSnapshot | null = null;
  @state() channelsError: string | null = null;
  @state() channelsLastSuccess: number | null = null;
  @state() configFormKey = 0;
  @state() whatsappLoginMessage: string | null = null;
  @state() whatsappLoginQrDataUrl: string | null = null;
  @state() whatsappLoginConnected: boolean | null = null;
  @state() whatsappBusy = false;

  @state() presenceLoading = false;
  @state() presenceEntries: PresenceEntry[] = [];
  @state() presenceError: string | null = null;
  @state() presenceStatus: string | null = null;

  @state() agentsLoading = false;
  @state() agentsList: AgentsListResult | null = null;
  @state() agentsError: string | null = null;
  @state() agentsSelectedId: string | null = null;
  @state() agentsPanel: "overview" | "files" | "tools" | "skills" | "tasks" | "channels" | "cron" =
    "overview";
  @state() agentTasksLoading = false;
  @state() agentTasksError: string | null = null;
  @state() agentTasks: TaskEntry[] = [];
  @state() agentFilesLoading = false;
  @state() agentFilesError: string | null = null;
  @state() agentFilesList: AgentsFilesListResult | null = null;
  @state() agentFileContents: Record<string, string> = {};
  @state() agentFileDrafts: Record<string, string> = {};
  @state() agentFileActive: string | null = null;
  @state() agentFileSaving = false;
  @state() agentIdentityLoading = false;
  @state() agentIdentityError: string | null = null;
  @state() agentIdentityById: Record<string, AgentIdentityResult> = {};
  @state() agentSkillsLoading = false;
  @state() agentSkillsError: string | null = null;
  @state() agentSkillsReport: SkillStatusReport | null = null;
  @state() agentSkillsAgentId: string | null = null;
  @state() agentProjectCronJobs: import("./controllers/igloobot.ts").CronJobEntry[] = [];
  @state() agentProjectCronJobsLoading = false;

  @state() sessionsLoading = false;
  @state() sessionsResult: SessionsListResult | null = null;
  @state() sessionsError: string | null = null;
  @state() sessionsFilterActive = "";
  @state() sessionsFilterLimit = "120";
  @state() sessionsIncludeGlobal = true;
  @state() sessionsIncludeUnknown = false;

  @state() usageLoading = false;
  @state() usageResult: import("./types.js").SessionsUsageResult | null = null;
  @state() usageCostSummary: import("./types.js").CostUsageSummary | null = null;
  @state() usageError: string | null = null;
  @state() usageStartDate = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  @state() usageEndDate = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  @state() usageSelectedSessions: string[] = [];
  @state() usageSelectedDays: string[] = [];
  @state() usageSelectedHours: number[] = [];
  @state() usageChartMode: "tokens" | "cost" = "tokens";
  @state() usageDailyChartMode: "total" | "by-type" = "by-type";
  @state() usageTimeSeriesMode: "cumulative" | "per-turn" = "per-turn";
  @state() usageTimeSeriesBreakdownMode: "total" | "by-type" = "by-type";
  @state() usageTimeSeries: import("./types.js").SessionUsageTimeSeries | null = null;
  @state() usageTimeSeriesLoading = false;
  @state() usageSessionLogs: import("./views/usage.js").SessionLogEntry[] | null = null;
  @state() usageSessionLogsLoading = false;
  @state() usageSessionLogsExpanded = false;
  // Applied query (used to filter the already-loaded sessions list client-side).
  @state() usageQuery = "";
  // Draft query text (updates immediately as the user types; applied via debounce or "Search").
  @state() usageQueryDraft = "";
  @state() usageSessionSort: "tokens" | "cost" | "recent" | "messages" | "errors" = "recent";
  @state() usageSessionSortDir: "desc" | "asc" = "desc";
  @state() usageRecentSessions: string[] = [];
  @state() usageTimeZone: "local" | "utc" = "local";
  @state() usageContextExpanded = false;
  @state() usageHeaderPinned = false;
  @state() usageSessionsTab: "all" | "recent" = "all";
  @state() usageVisibleColumns: string[] = [
    "channel",
    "agent",
    "provider",
    "model",
    "messages",
    "tools",
    "errors",
    "duration",
  ];
  @state() usageLogFilterRoles: import("./views/usage.js").SessionLogRole[] = [];
  @state() usageLogFilterTools: string[] = [];
  @state() usageLogFilterHasTools = false;
  @state() usageLogFilterQuery = "";

  // Non-reactive (don’t trigger renders just for timer bookkeeping).
  usageQueryDebounceTimer: number | null = null;

  @state() cronLoading = false;
  @state() cronJobs: CronJob[] = [];
  @state() cronStatus: CronStatus | null = null;
  @state() cronError: string | null = null;
  @state() cronForm: CronFormState = { ...DEFAULT_CRON_FORM };
  @state() cronRunsJobId: string | null = null;
  @state() cronRuns: CronRunLogEntry[] = [];
  @state() cronBusy = false;
  // Global cron merged view (project cron)
  @state() igGlobalCronJobs: import("./controllers/igloobot.ts").CronJobEntry[] = [];
  @state() igGlobalCronJobsLoading = false;
  @state() igGlobalCronProjectFilter: string | null = null;
  @state() igGlobalCronAgentFilter: string | null = null;

  @state() skillsLoading = false;
  @state() skillsReport: SkillStatusReport | null = null;
  @state() skillsError: string | null = null;
  @state() skillsFilter = "";
  @state() skillEdits: Record<string, string> = {};
  @state() skillsBusyKey: string | null = null;
  @state() skillMessages: Record<string, SkillMessage> = {};

  // Skills Settings panel (new dropdown-based UI)
  @state() skillsSettingsLoading = false;
  @state() skillsSettingsError: string | null = null;
  @state() skillsSettingsList: SkillSummary[] = [];
  @state() skillsSettingsSelectedKey: string | null = null;
  @state() skillsSettingsParams: SkillParametersResult | null = null;
  @state() skillsSettingsParamsLoading = false;
  @state() skillsSettingsParamsError: string | null = null;
  @state() skillsSettingsEdits: Record<string, string> = {};
  @state() skillsSettingsSaving: string | null = null;
  @state() skillsSettingsSaveMessage: { kind: "success" | "error"; message: string } | null = null;

  @state() debugLoading = false;
  @state() debugStatus: StatusSummary | null = null;
  @state() debugHealth: HealthSnapshot | null = null;
  @state() debugLifecheck: unknown = null;
  @state() debugCallMethod = "";
  @state() debugCallParams = "{}";
  @state() debugCallResult: string | null = null;
  @state() debugCallError: string | null = null;

  @state() systemOverviewLoading = false;
  @state() systemOverviewData: import("./controllers/system-overview.ts").SystemOverviewData | null =
    null;
  @state() systemOverviewError: string | null = null;
  @state() systemOverviewSubView: import("./controllers/system-overview.ts").SystemOverviewSubView =
    "tools-system";

  @state() agentPlansLoading = false;
  @state() agentPlans: import("./controllers/agent-plans.ts").AgentPlanEntry[] | null = null;
  @state() agentPlansError: string | null = null;
  @state() agentPlansStatusFilter: import("./controllers/agent-plans.ts").AgentPlansStatusFilter =
    "all";

  @state() tabVisibilityLoading = false;
  @state() tabVisibilityMap: import("./controllers/tab-visibility.ts").TabVisibilityMap = {};
  @state() tabVisibilityDirty: Set<string> = new Set();
  @state() tabVisibilitySaving = false;
  @state() tabVisibilityError: string | null = null;

  @state() roleSettingsLoading = false;
  @state() roleSettingsMap: import("./controllers/role-settings.ts").RoleSettingsMap = {};
  @state() roleSettingsDirty: Set<string> = new Set();
  @state() roleSettingsSaving = false;
  @state() roleSettingsError: string | null = null;

  @state() inferenceLoading = false;
  @state() inferenceEntries: import("./controllers/inference-config.ts").ModelPricingEntry[] = [];
  @state() inferenceDirty: Set<string> = new Set();
  @state() inferenceSaving = false;
  @state() inferenceError: string | null = null;

  @state() billingMaintenanceFeeCents = 1500;
  @state() billingSelectedPlanId: string | null = null;
  @state() billingExtraPerToken = false;
  @state() billingPlans: import("./controllers/inference-config.ts").BillingPlanEntry[] = [];
  @state() billingDirty = false;

  @state() billingLoading = false;
  @state() billingError: string | null = null;
  @state() billingSubscription: import("./controllers/billing.ts").SubscriptionEntry | null = null;
  @state() billingInvoices: import("./controllers/billing.ts").InvoiceEntry[] = [];
  @state() billingSaving = false;
  @state() billingSetupIntentSecret: string | null = null;
  @state() usageSubTab: "overview" | "sessions" | "invoices" = "overview";

  @state() igUserEmailCreds: import("./views/users-admin.ts").EmailCredEntry | null = null;
  @state() igUserEmailCredsLoading = false;
  @state() igUserEmailCredsSaving = false;
  @state() igUserEmailCredsError: string | null = null;
  @state() igUserEmailCredsTestResult: string | null = null;
  @state() igUserEmailCredsForm: Partial<import("./views/users-admin.ts").EmailCredEntry> | null = null;

  @state() logsLoading = false;
  @state() logsError: string | null = null;
  @state() logsFile: string | null = null;
  @state() logsEntries: LogEntry[] = [];
  @state() logsFilterText = "";
  @state() logsLevelFilters: Record<LogLevel, boolean> = {
    ...DEFAULT_LOG_LEVEL_FILTERS,
  };
  @state() logsAutoFollow = true;
  @state() logsTruncated = false;
  @state() logsCursor: number | null = null;
  @state() logsLastFetchAt: number | null = null;
  @state() logsLimit = 500;
  @state() logsMaxBytes = 250_000;
  @state() logsAtBottom = true;

  // Chat project session picklist
  @state() chatProjectId: string | null = null;
  @state() chatProjectSessions: ProjectSessionEntry[] = [];
  @state() chatProjectSessionsLoading = false;

  // Pinned projects session cache (sidebar)
  @state() pinnedProjectSessions: Record<string, ProjectSessionEntry[]> = {};
  @state() pinnedProjectsExpanded: Record<string, boolean> = {};
  @state() pinnedProjectSessionsLoading: Record<string, boolean> = {};

  // i-GlooBot platform stats (shown on Overview)
  @state() igStats: {
    projects: number;
    users: number;
    tasks: number;
    messages: number;
    sessions: number;
    avatarSessions: number;
    skills: number;
    n8nWorkflows: number;
    cronJobs: number;
    documents: number;
    emailConfigured: boolean;
  } | null = null;
  @state() igStatsAvailable = false; // true if the i-GlooBot plugin is loaded

  // Overview dashboard data (usage/cost/agents/providers)
  @state() overviewData: OverviewDataState = createOverviewDataState();

  // i-GlooBot admin state (Projects, Tasks, Users)
  @state() igProjects: ProjectEntry[] = [];
  @state() igProjectsLoading = false;
  @state() igProjectsError: string | null = null;
  @state() igSelectedProjectId: string | null = null;
  @state() igEditMode = false;
  @state() igEditForm: Partial<ProjectEntry> | null = null;
  @state() igTasks: TaskEntry[] = [];
  @state() igTasksLoading = false;
  @state() igTasksError: string | null = null;
  @state() igTaskStatusFilter: string | null = null;
  @state() igTaskTypeFilter: string | null = null;
  @state() igTaskProjectFilter: string | null = null;
  @state() igTaskAgentFilter: string | null = null;
  @state() igSelectedTaskId: string | null = null;
  @state() igTaskCreateMode = false;
  @state() igTaskCreateForm: Record<string, unknown> | null = null;
  @state() igUsers: UserEntry[] = [];
  @state() igUsersLoading = false;
  @state() igUsersError: string | null = null;
  @state() igUserSearchQuery = "";
  @state() igSelectedUserId: string | null = null;
  @state() igSelectedUserProjects: UserProjectAssignment[] = [];
  @state() igUserEditMode = false;
  @state() igUserEditForm: Partial<UserEntry> | null = null;

  // Company state (multi-company)
  @state() igCompanies: import("./views/company-admin.ts").CompanyEntry[] = [];
  @state() igCompaniesLoading = false;
  @state() igActiveCompanyId: string | null = null;
  @state() igCompany: import("./views/company-admin.ts").CompanyEntry | null = null;
  @state() igCompanyLoading = false;
  @state() igCompanyError: string | null = null;
  @state() igCompanySaving = false;
  @state() igCompanyForm: Partial<import("./views/company-admin.ts").CompanyEntry> | null = null;
  @state() igCompanyVatNumbers: Array<{
    id: string;
    companyId: string;
    country: string;
    vatNumber: string;
    vatRate: number;
    isDefault: boolean;
  }> = [];

  // Email Config state (singleton)
  @state() igEmailConfig: import("./views/email-admin.ts").EmailConfigEntry | null = null;
  @state() igEmailLoading = false;
  @state() igEmailError: string | null = null;
  @state() igEmailSaving = false;
  @state() igEmailForm: Partial<import("./views/email-admin.ts").EmailConfigEntry> | null = null;
  @state() igEmailTesting = false;
  @state() igEmailTestAddress = "";
  @state() igEmailTestResult: import("./views/email-admin.ts").EmailTestResult | null = null;
  @state() igEmailStatus: import("./views/email-admin.ts").EmailServiceStatus | null = null;

  // Channel Configs state (per-agent channel credentials)
  @state() ccSelectedAgentId: string | null = null;
  @state() ccSelectedChannelType: string | null = "telegram";
  @state() ccSelectedAccountId = "default";
  @state() ccAgents: import("./views/channel-configs-admin.ts").AgentOption[] = [];
  @state() ccConfig: import("./views/channel-configs-admin.ts").ChannelConfigEntry | null = null;
  @state() ccLoading = false;
  @state() ccError: string | null = null;
  @state() ccSaving = false;
  @state() ccForm: {
    credentials: Record<string, unknown>;
    settings: Record<string, unknown>;
    label: string;
    isActive: boolean;
  } | null = null;
  @state() ccTesting = false;
  @state() ccTestResult: import("./views/channel-configs-admin.ts").ChannelTestResult | null = null;
  @state() ccAllConfigs: import("./views/channel-configs-admin.ts").ChannelConfigEntry[] = [];
  @state() ccAllConfigsLoading = false;

  // Models state
  @state() modelsLoading = false;
  @state() modelsError: string | null = null;
  @state() modelCatalog: import("./views/models-admin.ts").ModelEntry[] = [];
  @state() providers: import("./views/models-admin.ts").ProviderEntry[] = [];
  @state() configHash: string | null = null;
  @state() defaultModel: string | null = null;
  @state() fallbackModels: string[] = [];
  @state() modelsSelectedProvider: string | null = null;
  @state() modelsProviderForm: import("./views/models-admin.ts").ProviderFormEntry | null = null;
  @state() modelsProviderFormDirty = false;
  @state() modelsEditingDefaults = false;
  @state() modelsDefaultModelDraft = "";
  @state() modelsFallbacksDraft: string[] = [];
  @state() modelsDisabledIds: string[] = [];
  @state() modelsManagedProviderIds: string[] = [];

  // Embedding state
  @state() embeddingModel: string | null = null;
  @state() embeddingDimension: number | null = null;
  @state() embeddingStatus: {
    provider: string;
    model: string | null;
    baseUrl: string | null;
    healthy: boolean;
    error?: string;
  } | null = null;
  @state() embeddingAvailableModels: Array<{ id: string; owned_by?: string }> = [];
  @state() embeddingLoading = false;
  @state() embeddingSaving = false;

  // Docling state
  @state() doclingUrl = "http://localhost:8001";
  @state() doclingModel: string | null = null;
  @state() doclingOcrLanguage = "eng";
  @state() doclingStatus: import("./controllers/models.ts").DoclingStatusResult | null = null;
  @state() doclingLoading = false;
  @state() doclingSaving = false;

  // i-GlooBot MCP Skills state
  @state() igMcpSkills: SkillEntry[] = [];
  @state() igMcpSkillsLoading = false;
  @state() igMcpSkillsError: string | null = null;
  @state() igMcpSkillsSearchQuery = "";
  @state() igMcpSelectedSkillId: string | null = null;
  @state() igMcpSkillEditMode = false;
  @state() igMcpSkillCreateMode = false;
  @state() igMcpSkillEditForm: Partial<SkillEntry> | null = null;
  @state() igMcpSkillsStatusFilter = "all";
  @state() igMcpSkillsSyncLoading = false;
  @state() igMcpSkillsSyncResult: { imported: number; skipped: number } | null = null;

  // i-GlooBot Avatar state
  @state() igAvatarStatus: AvatarServiceStatus | null = null;
  @state() igAvatarConfig: AvatarConfig | null = null;
  @state() igAvatarSessions: AvatarSessionEntry[] = [];
  @state() igAvatarLoading = false;
  @state() igAvatarError: string | null = null;
  @state() igAvatarConfigEditMode = false;
  @state() igAvatarConfigForm: Partial<AvatarConfig> | null = null;
  @state() igAvatarSelectedSessionId: string | null = null;
  @state() igAvatarStatusFilter = "";

  // N8N Workflows state
  @state() n8nWorkflows: import("./views/n8n-admin.ts").N8nWorkflowEntry[] = [];
  @state() n8nLoading = false;
  @state() n8nError: string | null = null;
  @state() n8nCreateMode = false;
  @state() n8nEditingId: string | null = null;
  @state() n8nForm: Partial<import("./views/n8n-admin.ts").N8nWorkflowEntry> | null = null;
  @state() n8nFilterProjectId: string | null = null;

  // Agent Definitions state
  @state() agentDefs: import("./controllers/agent-definitions.ts").AgentDefinitionEntry[] = [];
  @state() agentDefsLoading = false;
  @state() agentDefsError: string | null = null;
  @state() agentDefCreateMode = false;
  @state() agentDefEditingId: string | null = null;
  @state() agentDefForm: Partial<
    import("./controllers/agent-definitions.ts").AgentDefinitionEntry
  > | null = null;
  // Identity file overrides (dynamic agents)
  @state() agentDefIdFiles: import("./controllers/agent-definitions.ts").IdentityFileInfo[] = [];
  @state() agentDefIdFilesLoading = false;
  @state() agentDefIdFileActive: string | null = null;
  @state() agentDefIdFileDrafts: Record<string, string> = {};
  @state() agentDefIdFileSaving = false;

  // Computer Control state
  @state() computerStatus: import("./controllers/computer.ts").ComputerStatus | null = null;
  @state() computerConfig: import("./controllers/computer.ts").ComputerConfig | null = null;
  @state() computerLoading = false;
  @state() computerError: string | null = null;
  @state() computerScreenshot: string | null = null;
  @state() computerScreenshotLoading = false;

  // Documents Knowledge Base state
  @state() docsEntries: import("./views/documents-admin.ts").DocumentEntry[] = [];
  @state() docsLoading = false;
  @state() docsError: string | null = null;
  @state() docsFilterProjectId: string | null = null;
  @state() docsSearchQuery = "";
  @state() docsSearchResults: import("./views/documents-admin.ts").DocumentSearchResult[] = [];
  @state() docsSearching = false;
  @state() docsUploading = false;
  @state() docsReadFilename: string | null = null;
  @state() docsReadText: string | null = null;
  @state() docsReadLoading = false;

  // Project Agent Sessions
  @state() igProjectSessions: ProjectSessionEntry[] = [];
  @state() igProjectSessionsLoading = false;

  // Project Detail sub-state (used by the project detail panel)
  @state() igProjectDetailPanel:
    | "overview"
    | "skills"
    | "cron"
    | "tasks"
    | "users"
    | "contacts"
    | "sessions"
    | "knowledge-base"
    | "workspace" = "overview";
  @state() igProjectUsers: import("./controllers/igloobot.ts").ProjectUserEntry[] = [];
  @state() igProjectUsersLoading = false;
  @state() igProjectSkills: SkillEntry[] = [];
  @state() igProjectSkillsLoading = false;
  @state() igProjectCronJobs: import("./controllers/igloobot.ts").CronJobEntry[] = [];
  @state() igProjectCronJobsLoading = false;
  @state() igProjectTasks: TaskEntry[] = [];
  @state() igProjectTasksLoading = false;
  @state() igProjectContacts: import("./controllers/igloobot.ts").ProjectContactEntry[] = [];
  @state() igProjectContactsLoading = false;
  @state() igContactCreateMode = false;
  @state() igContactEditingId: string | null = null;
  @state() igCronCreateMode = false;
  @state() igCronCreateForm: Partial<import("./controllers/igloobot.ts").CronJobEntry> | null =
    null;
  @state() igCronEditingId: string | null = null;
  @state() igCronEditForm: Partial<import("./controllers/igloobot.ts").CronJobEntry> | null = null;
  @state() igSessionCreateMode = false;

  client: GatewayBrowserClient | null = null;
  private chatScrollFrame: number | null = null;
  private chatScrollTimeout: number | null = null;
  private chatHasAutoScrolled = false;
  private chatUserNearBottom = true;
  @state() chatNewMessagesBelow = false;
  private nodesPollInterval: number | null = null;
  private logsPollInterval: number | null = null;
  private debugPollInterval: number | null = null;
  private logsScrollFrame: number | null = null;
  private toolStreamById = new Map<string, ToolStreamEntry>();
  private toolStreamOrder: string[] = [];
  refreshSessionsAfterChat = new Set<string>();
  basePath = "";
  private popStateHandler = () =>
    onPopStateInternal(this as unknown as Parameters<typeof onPopStateInternal>[0]);
  private themeMedia: MediaQueryList | null = null;
  private themeMediaHandler: ((event: MediaQueryListEvent) => void) | null = null;
  private topbarObserver: ResizeObserver | null = null;

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    handleConnected(this as unknown as Parameters<typeof handleConnected>[0]);
  }

  protected firstUpdated() {
    handleFirstUpdated(this as unknown as Parameters<typeof handleFirstUpdated>[0]);
  }

  disconnectedCallback() {
    handleDisconnected(this as unknown as Parameters<typeof handleDisconnected>[0]);
    super.disconnectedCallback();
  }

  protected updated(changed: Map<PropertyKey, unknown>) {
    handleUpdated(this as unknown as Parameters<typeof handleUpdated>[0], changed);
  }

  connect() {
    connectGatewayInternal(this as unknown as Parameters<typeof connectGatewayInternal>[0]);
  }

  handleChatScroll(event: Event) {
    handleChatScrollInternal(
      this as unknown as Parameters<typeof handleChatScrollInternal>[0],
      event,
    );
  }

  handleLogsScroll(event: Event) {
    handleLogsScrollInternal(
      this as unknown as Parameters<typeof handleLogsScrollInternal>[0],
      event,
    );
  }

  exportLogs(lines: string[], label: string) {
    exportLogsInternal(lines, label);
  }

  resetToolStream() {
    resetToolStreamInternal(this as unknown as Parameters<typeof resetToolStreamInternal>[0]);
  }

  resetChatScroll() {
    resetChatScrollInternal(this as unknown as Parameters<typeof resetChatScrollInternal>[0]);
  }

  scrollToBottom(opts?: { smooth?: boolean }) {
    resetChatScrollInternal(this as unknown as Parameters<typeof resetChatScrollInternal>[0]);
    scheduleChatScrollInternal(
      this as unknown as Parameters<typeof scheduleChatScrollInternal>[0],
      true,
      Boolean(opts?.smooth),
    );
  }

  async loadAssistantIdentity() {
    await loadAssistantIdentityInternal(this);
  }

  applySettings(next: UiSettings) {
    applySettingsInternal(this as unknown as Parameters<typeof applySettingsInternal>[0], next);
  }

  setTab(next: Tab) {
    setTabInternal(this as unknown as Parameters<typeof setTabInternal>[0], next);
  }

  setTheme(next: ThemeMode, context?: Parameters<typeof setThemeInternal>[2]) {
    setThemeInternal(this as unknown as Parameters<typeof setThemeInternal>[0], next, context);
  }

  async loadOverview() {
    await loadOverviewInternal(this as unknown as Parameters<typeof loadOverviewInternal>[0]);
    // Also load usage/cost data for the redesigned overview dashboard
    this.overviewData.client = this.client;
    this.overviewData.connected = this.connected;
    this.overviewData.gatewayConnected = this.connected;
    this.overviewData.uptime =
      (this.hello?.snapshot as { uptimeMs?: number })?.uptimeMs
        ? `${Math.round((this.hello?.snapshot as { uptimeMs?: number }).uptimeMs! / 1000)}s`
        : "n/a";
    void loadOverviewData(this.overviewData);
  }

  async loadCron() {
    await loadCronInternal(this as unknown as Parameters<typeof loadCronInternal>[0]);
  }

  async handleAbortChat() {
    await handleAbortChatInternal(this as unknown as Parameters<typeof handleAbortChatInternal>[0]);
  }

  removeQueuedMessage(id: string) {
    removeQueuedMessageInternal(
      this as unknown as Parameters<typeof removeQueuedMessageInternal>[0],
      id,
    );
  }

  async handleSendChat(
    messageOverride?: string,
    opts?: Parameters<typeof handleSendChatInternal>[2],
  ) {
    await handleSendChatInternal(
      this as unknown as Parameters<typeof handleSendChatInternal>[0],
      messageOverride,
      opts,
    );
  }

  async handleWhatsAppStart(force: boolean) {
    await handleWhatsAppStartInternal(this, force);
  }

  async handleWhatsAppWait() {
    await handleWhatsAppWaitInternal(this);
  }

  async handleWhatsAppLogout() {
    await handleWhatsAppLogoutInternal(this);
  }

  async handleChannelConfigSave() {
    await handleChannelConfigSaveInternal(this);
  }

  async handleChannelConfigReload() {
    await handleChannelConfigReloadInternal(this);
  }

  async handleExecApprovalDecision(decision: "allow-once" | "allow-always" | "deny") {
    const active = this.execApprovalQueue[0];
    if (!active || !this.client || this.execApprovalBusy) {
      return;
    }
    this.execApprovalBusy = true;
    this.execApprovalError = null;
    try {
      await this.client.request("exec.approval.resolve", {
        id: active.id,
        decision,
      });
      this.execApprovalQueue = this.execApprovalQueue.filter((entry) => entry.id !== active.id);
    } catch (err) {
      this.execApprovalError = `Exec approval failed: ${String(err)}`;
    } finally {
      this.execApprovalBusy = false;
    }
  }

  handleGatewayUrlConfirm() {
    const nextGatewayUrl = this.pendingGatewayUrl;
    if (!nextGatewayUrl) {
      return;
    }
    this.pendingGatewayUrl = null;
    applySettingsInternal(this as unknown as Parameters<typeof applySettingsInternal>[0], {
      ...this.settings,
      gatewayUrl: nextGatewayUrl,
    });
    this.connect();
  }

  handleGatewayUrlCancel() {
    this.pendingGatewayUrl = null;
  }

  // Sidebar handlers for tool output viewing
  handleOpenSidebar(content: string) {
    if (this.sidebarCloseTimer != null) {
      window.clearTimeout(this.sidebarCloseTimer);
      this.sidebarCloseTimer = null;
    }
    this.sidebarContent = content;
    this.sidebarError = null;
    this.sidebarOpen = true;
  }

  handleCloseSidebar() {
    this.sidebarOpen = false;
    // Clear content after transition
    if (this.sidebarCloseTimer != null) {
      window.clearTimeout(this.sidebarCloseTimer);
    }
    this.sidebarCloseTimer = window.setTimeout(() => {
      if (this.sidebarOpen) {
        return;
      }
      this.sidebarContent = null;
      this.sidebarError = null;
      this.sidebarCloseTimer = null;
    }, 200);
  }

  handleSplitRatioChange(ratio: number) {
    const newRatio = Math.max(0.4, Math.min(0.7, ratio));
    this.splitRatio = newRatio;
    this.applySettings({ ...this.settings, splitRatio: newRatio });
  }

  handleLogin(result: { user: AuthUser; token: string }) {
    this.isAuthenticated = true;
    this.authToken = result.token;
    this.currentUser = result.user;
    if (this.client) {
      this.client.appAuthToken = result.token;
    }
    saveAuth({ authToken: result.token, currentUser: result.user });
  }

  handleLogout() {
    this.isAuthenticated = false;
    this.authToken = null;
    this.currentUser = null;
    if (this.client) {
      this.client.appAuthToken = null;
    }
    clearAuth();
  }

  render() {
    return renderApp(this as unknown as AppViewState);
  }
}
